<template>
  <MarketingPageLayout>
    <div v-if="!item" class="py-12">
      <h1 class="mb-4 text-3xl font-semibold tracking-tight text-ink">Page not found</h1>
      <p class="text-ink-2">
        The page you’re looking for doesn’t exist yet, or hasn’t been published.
      </p>
      <router-link to="/" class="mt-4 inline-block font-medium text-accent hover:text-accent-hover">
        ← Back to home
      </router-link>
    </div>

    <template v-else>
      <!-- Visible breadcrumb (mirrors the BreadcrumbList JSON-LD) -->
      <nav aria-label="Breadcrumb" class="mb-8 flex flex-wrap items-center gap-2 text-sm text-ink-4">
        <router-link to="/" class="transition-colors hover:text-ink">Home</router-link>
        <span aria-hidden="true" class="text-control">/</span>
        <router-link :to="folderLabel.href" class="transition-colors hover:text-ink">{{ folderLabel.name }}</router-link>
        <template v-if="folder !== 'pricing'">
          <span aria-hidden="true" class="text-control">/</span>
          <span class="text-ink-2">{{ item.title || item.slug }}</span>
        </template>
      </nav>

      <article>
        <header class="mb-10 border-b border-line pb-8">
          <p class="mb-4 font-mono text-[12px] uppercase tracking-[0.14em] text-ink-4">{{ eyebrowLabel }}</p>
          <h1 class="text-4xl font-semibold leading-[1.05] tracking-[-0.03em] text-ink md:text-5xl">
            {{ item.title || item.slug }}
          </h1>
          <p
            v-if="item.description"
            class="mt-4 text-lg leading-relaxed text-ink-2 md:text-xl"
          >{{ item.description }}</p>
          <div class="mt-6 flex flex-wrap items-center gap-x-6 gap-y-2 text-sm text-ink-4">
            <span v-if="folder === 'blog'" class="flex items-center gap-2">
              <span class="flex h-7 w-7 items-center justify-center rounded-full bg-accent-tint text-xs font-semibold text-accent">
                {{ authorInitials }}
              </span>
              <span class="text-ink-2">{{ authorName }}</span>
            </span>
            <span v-if="formattedDate">{{ folder === 'blog' ? 'Published' : 'Updated' }} {{ formattedDate }}</span>
            <span v-if="formattedUpdated">Updated {{ formattedUpdated }}</span>
            <span v-if="readingTimeMin">{{ readingTimeMin }} min read</span>
          </div>
        </header>

        <p
          v-if="heroAnswer"
          class="mb-10 rounded-2xl border border-line border-l-4 border-l-accent bg-surface px-6 py-5 text-lg leading-relaxed text-ink md:text-xl"
        >{{ heroAnswer }}</p>

        <!-- Side-by-side comparison table — driven by frontmatter.comparison
             on /compare/:slug pages so each comparison can have its own products + rows. -->
        <ComparisonTable
          v-if="comparisonData"
          v-bind="comparisonData"
          class="-mx-4 my-10 sm:-mx-6 lg:-mx-8"
        />

        <div class="prose prose-lg max-w-none" v-html="bodyHtml" />

        <FaqBlock
          v-if="faqItems.length"
          class="-mx-4 mt-16 sm:-mx-6 lg:-mx-8"
          heading="Frequently asked questions"
          :items="faqItems"
        />

        <AuthorBio
          v-if="folder === 'blog'"
          :name="authorName"
          :job-title="authorJobTitle"
          :organization="authorOrganization"
          :organization-url="authorOrganizationUrl"
          :bio="authorBio"
          :same-as="authorSameAs"
          v-bind="authorPhoto ? { photo: authorPhoto } : {}"
        />

        <CtaBlock
          class="mt-12"
          :primary="{ label: 'Try the live demo', to: '/demo', eventName: 'cta_demo' }"
          :secondary="{ label: 'Start free, up to 5 users', to: '/login', eventName: 'cta_trial' }"
          heading="See it in action"
          subheading="A read-only demo with phone-heavy sales data, ready in one click. No signup required."
        />

        <!-- More from this section -->
        <section
          v-if="moreFromSection.length"
          class="mt-16 border-t border-line pt-10"
        >
          <h2 class="mb-6 text-xl font-semibold tracking-tight text-ink md:text-2xl">
            More from {{ folderLabel.name.toLowerCase() }}
          </h2>
          <ul class="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <li v-for="rel in moreFromSection" :key="rel.slug">
              <router-link
                :to="`/${folder}/${rel.slug}`"
                class="block rounded-2xl border border-line bg-surface p-5 transition-colors hover:border-accent-line"
              >
                <p class="mb-1 font-semibold text-ink">{{ rel.title || rel.slug }}</p>
                <p
                  v-if="rel.description"
                  class="text-sm leading-relaxed text-ink-2"
                >{{ rel.description }}</p>
              </router-link>
            </li>
          </ul>
        </section>

      </article>
    </template>
  </MarketingPageLayout>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { useRoute } from 'vue-router'
import { getAllContent, getContentBySlug, type ContentFolder } from '@/composables/useContent'
import { useSeo, seoConstants } from '@/composables/useSeo'
import MarketingPageLayout from '@/components/marketing/MarketingPageLayout.vue'
import FaqBlock, { type FaqItem } from '@/components/seo/FaqBlock.vue'
import AuthorBio, { type SocialLink } from '@/components/seo/AuthorBio.vue'
import ComparisonTable, { type ComparisonProduct, type ComparisonRow } from '@/components/seo/ComparisonTable.vue'
import CtaBlock from '@/components/marketing/CtaBlock.vue'
import { FOUNDER, SOFTWARE_ID, WEBSITE_ID, orgRef, personLd, softwareApplicationLd } from '@/lib/seoEntities'

const props = defineProps<{
  /** Forces a folder when the same component is used by multiple route meta values. */
  folder?: ContentFolder
  /** For singleton pages (e.g., pricing) — fixed slug instead of using route param. */
  slug?: string
}>()

const route = useRoute()
const folder = computed<ContentFolder>(
  () => props.folder || ((route.meta.contentFolder as ContentFolder | undefined) ?? 'blog')
)
const slug = computed(() => props.slug || (route.params.slug as string))
const item = computed(() => getContentBySlug(folder.value, slug.value))

const heroAnswer = computed(() => item.value?.frontmatter?.heroAnswer as string | undefined)

interface FrontmatterComparison {
  heading?: string
  subheading?: string
  products: ComparisonProduct[]
  rows: ComparisonRow[]
}
const comparisonData = computed<FrontmatterComparison | undefined>(() => {
  const raw = item.value?.frontmatter?.comparison as FrontmatterComparison | undefined
  if (!raw || !Array.isArray(raw.products) || !Array.isArray(raw.rows)) return undefined
  return raw
})

const faqItems = computed<FaqItem[]>(() => {
  const raw = item.value?.frontmatter?.faq as Array<FaqItem> | undefined
  return Array.isArray(raw) ? raw : []
})

const authorName = computed(() => (item.value?.frontmatter?.authorName as string) || 'Md Sohel Rana')
const authorJobTitle = computed(() => (item.value?.frontmatter?.authorJobTitle as string) || 'Founder')
const authorOrganization = computed(() => (item.value?.frontmatter?.authorOrganization as string) || 'Nerddevs')
const authorOrganizationUrl = computed(() => (item.value?.frontmatter?.authorOrganizationUrl as string) || 'https://nerddevs.com')
const authorPhoto = computed(
  () =>
    (item.value?.frontmatter?.authorPhoto as string | undefined) ||
    (authorName.value === FOUNDER.name ? '/og/sohel-headshot.jpg' : undefined),
)
const authorBio = computed(
  () =>
    (item.value?.frontmatter?.authorBio as string) ||
    'Md Sohel Rana is the founder of Nerddevs, a Bangladesh-based product team. He builds NerdCRM and Biddaan and writes here about phone-heavy sales operations.'
)
const authorSameAs = computed<SocialLink[]>(() => {
  const raw = item.value?.frontmatter?.authorSameAs as SocialLink[] | undefined
  const given = Array.isArray(raw) ? raw : []
  if (authorName.value !== FOUNDER.name) return given
  // The founder's public profiles are always listed, so every post links author → identity.
  const seen = new Set(given.map((l) => l.url.replace(/\/$/, '')))
  return [...given, ...FOUNDER.links.filter((l) => !seen.has(l.url.replace(/\/$/, '')))]
})

// updatedAt (optional frontmatter) marks a meaningful revision; feeds dateModified,
// article:modified_time and the sitemap's <lastmod>.
const updatedAt = computed(() => {
  const raw = item.value?.frontmatter?.updatedAt as string | undefined
  const d = raw ? new Date(raw) : null
  return d && !Number.isNaN(d.getTime()) ? d : null
})

const fmtDate = (d: Date) =>
  d.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric', timeZone: 'UTC' })

// Blog posts show their publish date (plus "Updated" when revised); other pages show
// when they were last updated.
const formattedDate = computed(() => {
  const d = folder.value === 'blog' ? item.value?.publishedAt : updatedAt.value || item.value?.publishedAt
  return d ? fmtDate(d) : ''
})
const formattedUpdated = computed(() => {
  if (folder.value !== 'blog' || !updatedAt.value || !item.value?.publishedAt) return ''
  return updatedAt.value.getTime() > item.value.publishedAt.getTime() ? fmtDate(updatedAt.value) : ''
})

// Eyebrow text shown above the article title — folder-specific so visitors
// instantly know what kind of page they're reading.
const eyebrowByFolder: Record<ContentFolder, string> = {
  blog: 'Field notes',
  compare: 'Comparison',
  'use-cases': 'Use case',
  features: 'Feature',
  help: 'How-to',
  pricing: 'Pricing',
}
const eyebrowLabel = computed(() => eyebrowByFolder[folder.value] || 'NerdCRM')

// Used for the visible breadcrumb at the top of the article.
const folderLabelByFolder: Record<ContentFolder, { name: string; href: string }> = {
  blog: { name: 'Blog', href: '/blog' },
  compare: { name: 'Compare', href: '/compare/zoho-bigin' },
  'use-cases': { name: 'Use cases', href: '/use-cases/phone-heavy-sales' },
  features: { name: 'Features', href: '/features/call-recording' },
  help: { name: 'Help', href: '/help' },
  pricing: { name: 'Pricing', href: '/pricing' },
}
const folderLabel = computed(() => folderLabelByFolder[folder.value])

const authorInitials = computed(() => {
  const parts = (authorName.value || '').trim().split(/\s+/).slice(0, 2)
  return parts.map((p) => p[0]?.toUpperCase() || '').join('') || 'N'
})

// Reading time estimate based on a 220 wpm pace. Strips HTML tags from the
// rendered body before counting; not pixel-perfect but close enough that "5 min
// read" reflects the actual length of a 1100-word article.
const readingTimeMin = computed(() => {
  if (!item.value?.html) return 0
  const text = item.value.html.replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim()
  const words = text ? text.split(' ').length : 0
  return Math.max(1, Math.round(words / 220))
})

// "More from this section" — up to 3 other items from the same folder, newer
// first. Skips the current item and pricing (singleton).
const moreFromSection = computed(() => {
  if (!item.value || folder.value === 'pricing') return []
  return getAllContent(folder.value)
    .filter((c) => c.slug !== item.value!.slug)
    .slice(0, 3)
})

// The header already renders the title as the page's one <h1>, so drop the
// markdown's own leading "# Title" from the body (two h1s hurt SEO).
const bodyHtml = computed(() => (item.value?.html || '').replace(/^\s*<h1[^>]*>[\s\S]*?<\/h1>\s*/, ''))

// Share image per section; every blog post has its own (scripts/generate-og-images.mjs).
const ogImageByFolder: Partial<Record<ContentFolder, string>> = {
  compare: 'compare.png',
  'use-cases': 'use-case.png',
  features: 'feature.png',
}
const ogImage = computed(() => {
  if (!item.value) return undefined
  const custom = item.value.frontmatter?.ogImage as string | undefined
  if (custom) return custom.startsWith('http') ? custom : `${seoConstants.SITE_URL}${custom}`
  if (folder.value === 'blog') return `${seoConstants.SITE_URL}/og/blog/${item.value.slug}.png`
  const file = ogImageByFolder[folder.value]
  return file ? `${seoConstants.SITE_URL}/og/${file}` : undefined
})

const folderToOgType: Record<ContentFolder, 'website' | 'article' | 'product'> = {
  blog: 'article',
  compare: 'product',
  'use-cases': 'website',
  features: 'product',
  help: 'article',
  pricing: 'website',
}

const path = computed(() => {
  if (!item.value) return route.fullPath
  if (folder.value === 'pricing') return '/pricing'
  return `/${folder.value}/${item.value.slug}`
})

// Article for blog posts, TechArticle for help guides. Author, publisher, site and
// product are the shared entities from lib/seoEntities (same @ids on every page).
const articleSchema = computed(() => {
  if (!item.value || (folder.value !== 'blog' && folder.value !== 'help')) return undefined
  const published = item.value.publishedAt?.toISOString()
  const modified = (updatedAt.value || item.value.publishedAt)?.toISOString()
  const url = `${seoConstants.SITE_URL}${path.value}`
  return {
    '@context': 'https://schema.org',
    '@type': folder.value === 'help' ? 'TechArticle' : 'Article',
    headline: item.value.title,
    description: item.value.description,
    ...(ogImage.value ? { image: ogImage.value } : {}),
    ...(published ? { datePublished: published } : {}),
    ...(modified ? { dateModified: modified } : {}),
    inLanguage: 'en',
    author: personLd({
      name: authorName.value,
      jobTitle: authorJobTitle.value,
      ...(authorPhoto.value ? { photo: authorPhoto.value } : {}),
      sameAs: authorSameAs.value,
    }),
    publisher: orgRef,
    isPartOf: { '@id': WEBSITE_ID },
    about: { '@id': SOFTWARE_ID },
    mainEntityOfPage: { '@type': 'WebPage', '@id': url },
    url,
  }
})

// BreadcrumbList — Home → Section → Current page. Pricing is a singleton
// so its breadcrumb is just Home → Pricing. Reuses folderLabel above.
const breadcrumbSchema = computed(() => {
  if (!item.value) return undefined
  const section = folderLabel.value
  const items: Array<Record<string, unknown>> = [
    { '@type': 'ListItem', position: 1, name: 'Home', item: `${seoConstants.SITE_URL}/` },
    { '@type': 'ListItem', position: 2, name: section.name, item: `${seoConstants.SITE_URL}${section.href}` },
  ]
  if (folder.value !== 'pricing') {
    items.push({ '@type': 'ListItem', position: 3, name: item.value.title || item.value.slug, item: `${seoConstants.SITE_URL}${path.value}` })
  }
  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: items,
  }
})

const jsonLdBlocks = computed(() => {
  const blocks: Array<Record<string, unknown>> = []
  if (articleSchema.value) blocks.push(articleSchema.value)
  // Product pages carry the full SoftwareApplication (price, offers, features) so an
  // engine landing on any of them gets the facts without needing the home page.
  if (item.value && ['pricing', 'features', 'compare', 'use-cases'].includes(folder.value)) {
    blocks.push(softwareApplicationLd())
  }
  if (breadcrumbSchema.value) blocks.push(breadcrumbSchema.value)
  return blocks
})

// seoTitle / seoDescription (optional frontmatter) keep the <title> and meta
// description within what Google shows; the page keeps its full title and intro.
const seoOpts = {
  title: (item.value?.frontmatter?.seoTitle as string | undefined) || item.value?.title || 'NerdCRM',
  description: (item.value?.frontmatter?.seoDescription as string | undefined)
    || item.value?.description || 'NerdCRM — a $4/seat CRM with auto-linked call recording.',
  ...(ogImage.value ? { ogImage: ogImage.value } : {}),
  path: path.value,
  ogType: folderToOgType[folder.value] || 'website',
  rawTitle: false,
  noIndex: !item.value,
  ...(item.value?.publishedAt ? { publishedTime: item.value.publishedAt.toISOString() } : {}),
  ...(updatedAt.value ? { modifiedTime: updatedAt.value.toISOString() } : {}),
  ...(jsonLdBlocks.value.length ? { jsonLd: jsonLdBlocks.value } : {}),
}
useSeo(seoOpts)
</script>

<style>
.prose h1 {
  @apply mb-4 text-4xl font-semibold leading-tight tracking-[-0.02em] text-ink md:text-5xl;
}
.prose h2 {
  @apply mb-4 mt-12 text-2xl font-semibold tracking-[-0.02em] text-ink md:text-3xl;
}
.prose h3 {
  @apply mb-3 mt-8 text-xl font-semibold text-ink;
}
.prose p {
  @apply mb-4 leading-relaxed text-ink-2;
}
.prose ul {
  @apply mb-4 list-disc space-y-2 pl-6 text-ink-2;
}
.prose ol {
  @apply mb-4 list-decimal space-y-2 pl-6 text-ink-2;
}
.prose a {
  @apply font-medium text-accent hover:text-accent-hover hover:underline;
}
.prose strong {
  @apply font-semibold text-ink;
}
.prose code {
  @apply rounded bg-wash px-1.5 py-0.5 font-mono text-[0.9em] text-ink;
}
.prose pre {
  @apply my-6 overflow-x-auto rounded-2xl border border-line bg-surface p-5 text-sm;
}
.prose pre code {
  @apply bg-transparent p-0;
}
.prose blockquote {
  @apply my-6 border-l-4 border-accent-line pl-4 italic text-ink-2;
}
.prose img {
  @apply my-8 rounded-2xl border border-line;
}
/* Markdown tables (pricing math, comparisons, recommendation lists). Block display lets
   wide tables scroll sideways on phones instead of widening the page. */
.prose table {
  @apply my-8 block w-full overflow-x-auto border-collapse text-base;
}
.prose th {
  @apply border-b border-line bg-wash px-4 py-3 text-left align-bottom font-semibold text-ink;
}
.prose td {
  @apply border-b border-line-soft px-4 py-3 align-top text-ink-2;
}
</style>
